import { observer } from 'mobx-react-lite';
import { ArrowForwardIconSvg, DeleteIcon, InfoIcon, LightBulbIcon } from '../../../../lib/icons';
import { ChangeEvent, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateBenefitSet } from '../hooks/useCreateBenefitSet';
import { Grid, Tab, Tabs, ThemeProvider, Input as MuiInput, FormHelperText } from '@mui/material';
import { useStepper } from '../../partials/actionStepperPartial';
import { Card, ConfirmationDialog } from '../../../../lib/components';
import { Button } from '../../../../lib/components/atomic/button';
import { InputFieldLabel } from '../../../../lib/components/atomic/input/shared/fieldLabel';
import { InfoSection } from '../../../../lib/components/atomic/typography/InfoSection';
import { descriptionTheme, tabTheme } from '../../createCustomBenefit/steps/benefitProfile';
import { TextField } from '../../../../lib/components/atomic/input/textField';
import { Drawer } from '../../../../lib/components/atomic/dataDisplay/drawer';
import { theme } from '../../../../lib/theme';
import { enqueueSnackbar } from 'notistack';

export const Description = observer(() => {
  const { t } = useTranslation();
  const { newBenefitSet } = useCreateBenefitSet();
  const { setNextButtonConfig, activeStep, setActiveStep, showInfoDrawer, setShowInfoDrawer } =
    useStepper();
  const [tabValue, setTabValue] = useState(0);
  const [showDeleteTranslationDialog, setShowDeleteTranslationDialog] = useState(false);
  const [nameError, setNameError] = useState<string | undefined>(undefined);
  const [descriptionError, setDescriptionError] = useState<string | undefined>(undefined);
  const NAME_MAX_LENTGH = 40;
  const DESCRIPTION_MAX_LENGTH = 800;

  useEffect(() => {
    setNextButtonConfig({
      text: t('common:NEXT'),
      endIcon: <ArrowForwardIconSvg />,
      onClick: () => {
        if (!newBenefitSet.name_de) {
          setNameError(t('benefitSet:nameMissingError'));
        } else if (newBenefitSet.translationAdded && !newBenefitSet.name_en) {
          setNameError(t('benefitSet:nameMissingError'));
          enqueueSnackbar({
            message: t('benefitSet:errorMissingNameEN'),
            variant: 'error'
          });
        } else {
          setActiveStep(activeStep + 1);
        }
      }
    });
  }, []);

  const handleTranslationButtonClick = () => {
    if (newBenefitSet.translationAdded) {
      setShowDeleteTranslationDialog(true);
    } else {
      newBenefitSet.translationAdded = true;
      setTabValue(1);
    }
  };

  const handleNameChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (/^[a-zA-Z0-9\säöüÄÖÜß-]*$/.test(value) && value.length <= NAME_MAX_LENTGH) {
      setNameError(undefined);
      newBenefitSet[name === 'name_de' ? 'name_de' : 'name_en'] = value;
    } else {
      setNameError(t('benefitSet:nameError'));
    }
  };

  const handleDescriptionChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    if (value.length > DESCRIPTION_MAX_LENGTH) {
      setDescriptionError(t('custom:description_error'));
    } else {
      setDescriptionError(undefined);
      newBenefitSet[name === 'description_de' ? 'description_de' : 'description_en'] = value;
    }
  };

  const handleTabChange = (event: ChangeEvent<object>, newValue: number) => setTabValue(newValue);

  return (
    <>
      <Grid container direction="row" md={12}>
        <Grid container alignItems="center">
          <Grid item marginTop={-2} marginBottom={-2}>
            <ThemeProvider theme={tabTheme}>
              <Tabs
                value={tabValue}
                onChange={handleTabChange}
                indicatorColor="primary"
                sx={{
                  '& .MuiTabs-indicator': {
                    display: 'flex',
                    justifyContent: 'center',
                    backgroundColor: 'transparent'
                  },
                  '& .MuiTabs-indicatorSpan': {
                    width: 'calc(100% - 20px)',
                    backgroundColor: theme.palette.heaven.main
                  },
                  '& .MuiTab-root': {
                    marginLeft: -1,
                    padding: 0,
                    color: theme.palette.primary.dark,
                    '&.Mui-selected': {
                      fontWeight: '400 !important',
                      color: theme.palette.heaven.main
                    }
                  }
                }}
                TabIndicatorProps={{
                  children: <span className="MuiTabs-indicatorSpan" />
                }}
              >
                <Tab label={t('custom:german')} />
                {newBenefitSet.translationAdded && (
                  <Tab label={t('custom:english')} className="!ml-3" />
                )}
              </Tabs>
            </ThemeProvider>
          </Grid>
          <Grid item marginTop={-2} marginBottom={-2}>
            <Button variant="tertiary" onClick={handleTranslationButtonClick}>
              {newBenefitSet.translationAdded
                ? t('custom:delete_translation')
                : t('custom:add_translation')}

              {newBenefitSet.translationAdded && <DeleteIcon />}
            </Button>
          </Grid>
        </Grid>
        {tabValue === 0 && (
          <Grid container md={8} spacing={3} marginTop={0.5}>
            <Grid item xs={12} md={12}>
              <Grid container marginBottom={2}>
                <Grid item md={12}>
                  <TextField
                    name={'name_de'}
                    label={t('benefitSet:title')}
                    type="text"
                    placeholder={t('benefitSet:namePlaceholder')}
                    value={newBenefitSet.name_de}
                    onChange={handleNameChange}
                    error={!!nameError && !newBenefitSet.name_de}
                    helperText={
                      nameError ?? `${newBenefitSet.name_de?.length ?? 0} / ${NAME_MAX_LENTGH}`
                    }
                    required={true}
                  />
                </Grid>
              </Grid>
              <div>
                <InputFieldLabel label={t('benefitSet:description') + ' ' + t('common:OPTIONAL')} />
                <ThemeProvider theme={descriptionTheme}>
                  <MuiInput
                    name="description_de"
                    multiline={true}
                    minRows={8}
                    value={newBenefitSet.description_de}
                    disableUnderline
                    onChange={handleDescriptionChange}
                    placeholder={t('benefitSet:descriptionPlaceholder')}
                    sx={{
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                  />
                  <FormHelperText
                    sx={(theme) => ({
                      marginLeft: '12px',
                      color: descriptionError ? theme.palette.error.main : theme.palette.grey[600]
                    })}
                  >
                    {`${newBenefitSet.description_de?.length || 0} / ${DESCRIPTION_MAX_LENGTH}`}
                  </FormHelperText>
                </ThemeProvider>
              </div>
            </Grid>
          </Grid>
        )}
        {tabValue === 1 && (
          <Grid container md={8} spacing={3} marginTop={0.5}>
            <Grid item xs={12} md={12}>
              <Grid container marginBottom={2}>
                <Grid item md={12}>
                  <TextField
                    title={'name_en'}
                    label={t('benefitSet:title')}
                    type="text"
                    placeholder={t('benefitSet:namePlaceholder')}
                    value={newBenefitSet.name_en}
                    onChange={handleNameChange}
                    error={!!nameError && !newBenefitSet.name_en}
                    helperText={nameError ?? `${newBenefitSet.name_en?.length ?? 0} / 40`}
                    required={true}
                  />
                </Grid>
              </Grid>
              <div>
                <InputFieldLabel label={t('benefitSet:description') + ' ' + t('common:OPTIONAL')} />
                <ThemeProvider theme={descriptionTheme}>
                  <MuiInput
                    name="description_en"
                    multiline={true}
                    minRows={8}
                    value={newBenefitSet.description_en}
                    disableUnderline
                    onChange={(e) => (newBenefitSet.description_en = e.target.value)}
                    placeholder={t('benefitSet:descriptionPlaceholder')}
                    sx={{
                      borderRadius: '8px',
                      padding: '12px'
                    }}
                  />
                  <FormHelperText
                    sx={(theme) => ({
                      marginLeft: '12px',
                      color: descriptionError ? theme.palette.error.main : theme.palette.grey[600]
                    })}
                  >
                    {`${newBenefitSet.description_en?.length || 0} / 800`}
                  </FormHelperText>
                </ThemeProvider>
              </div>
            </Grid>
          </Grid>
        )}
        <Grid container md={4} alignItems="center" sx={{ marginLeft: 3, marginTop: 2 }}>
          <Grid item xs={12} md={12} sx={{ marginTop: 2 }}>
            <Card
              title={t('benefitSet:exampleTitle', {
                name: 'Vollzeit'
              })}
              icon={<LightBulbIcon />}
            >
              <InfoSection
                content={t('benefitSet:exampleTexts', { returnObjects: true })}
                type="card"
              />
              <div className="flex text-xs text-grey-600 gap-x-2">
                <InfoIcon className="h-4 w-4" />
                {t('benefitSet:exampleHint')}
              </div>
            </Card>
          </Grid>
        </Grid>
      </Grid>

      <ConfirmationDialog
        open={showDeleteTranslationDialog}
        title={t('custom:discard_translation')}
        contentText={t('custom:discard_translation_hint')}
        onConfirm={() => {
          newBenefitSet.clear_en();
          setShowDeleteTranslationDialog(false);
          setTabValue(0);
        }}
        onClose={() => setShowDeleteTranslationDialog(false)}
        onCancel={() => setShowDeleteTranslationDialog(false)}
        confirmButtonText={t('custom:delete')}
        cancelButtonText={t('common:CANCEL')}
        hideCancelButton={false}
        variant={'delete'}
      />
      <Drawer
        label={t('benefitSet:descriptionDrawerTitle')}
        open={showInfoDrawer}
        closeDrawer={() => setShowInfoDrawer(false)}
      >
        <div className="flex flex-col gap-y-6">
          <InfoSection content={t('benefitSet:descriptionDrawerTexts', { returnObjects: true })} />
          <div className="flex text-xs text-grey-600 gap-x-2">
            <InfoIcon className="h-4 w-4" />
            {t('benefitSet:descriptionDrawerHint')}
          </div>
        </div>
      </Drawer>
    </>
  );
});
